/**
 * V2 Flexible Layout — Media Section Configuration
 *
 * Gradient configuration for FlexibleLayout Media sections.
 * Unlike the paywall gradient (content.paywall.bodyAppearance.overlay),
 * FlexibleLayout gradient lives on the media appearance itself:
 *   content.featuredMedia.appearance.gradient (FeaturedMediaStyle.gradient)
 *
 * Re-exports V2_DEFAULT_MEDIA_STRUCTURE from v2MediaConfig for media enable/disable.
 */

import type { FieldConfig } from '../types/editorFieldTypes';
import {
  SCALE_MODE_OPTIONS,
  NUMBER_0_100_OPTIONS,
  NUMBER_0_50_OPTIONS,
  BOOLEAN_OPTIONS,
} from './atomicFields/options';

// Re-export media defaults from existing V2 media config
export { V2_DEFAULT_MEDIA_STRUCTURE, V2_MEDIA_KIND_OPTIONS, V2_DEFAULT_IMAGE_CONTENT, V2_DEFAULT_VIDEO_CONTENT } from './v2MediaConfig';
export type { V2MediaKind } from './v2MediaConfig';

// ============================================
// Gradient Configuration (FlexibleLayout paths)
// ============================================

/**
 * Default gradient structure for FlexibleLayout media sections.
 * Bottom-to-top gradient: opaque color at bottom, transparent at top.
 */
export const V2_FLEXIBLE_GRADIENT_STRUCTURE = {
  colors: ['#000000', '#00000000'],
  locations: [0, 1],
  startPoint: { x: 0.5, y: 1 },
  endPoint: { x: 0.5, y: 0 },
  kind: 'GradientKind1',
  heightPercentage: 50,
};

export const V2_FLEXIBLE_GRADIENT_COLOR_CONFIG: FieldConfig = {
  path: 'content.featuredMedia.appearance.gradient.colors.0',
  label: 'Color',
  type: 'color',
  defaultValue: '#000000',
  isNullable: false,
};

// ============================================
// Media Appearance Fields (FeaturedMediaStyle)
// ============================================

/**
 * Appearance field configs for FlexibleMediaSection and V2MediaSection.
 * Maps to FeaturedMediaStyle: scaleMode, dimensions.heightPercentage, cornerRadii.uniform.
 */
export const V2_MEDIA_APPEARANCE_FIELDS: FieldConfig[] = [
  {
    path: 'content.featuredMedia.appearance.scaleMode',
    label: 'Scale Mode',
    type: 'select',
    options: [...SCALE_MODE_OPTIONS],
    defaultValue: 'scaleAspectFit',
    isNullable: true,
  },
  {
    path: 'content.featuredMedia.appearance.dimensions.heightPercentage',
    label: 'Height %',
    type: 'numberCombo',
    options: NUMBER_0_100_OPTIONS,
    defaultScrollValue: 50,
    isNullable: true,
  },
  {
    path: 'content.featuredMedia.appearance.cornerRadii.uniform',
    label: 'Corner Radius',
    type: 'numberCombo',
    options: NUMBER_0_50_OPTIONS,
    defaultScrollValue: 0,
    isNullable: true,
  },
];

/** Screen media top alignment field. */
export const V2_MEDIA_TOP_ALIGNMENT_FIELD: FieldConfig = {
  path: 'content.featuredMedia.appearance.topAlignment',
  label: 'Media Top',
  type: 'select',
  options: [
    { value: 'top', label: 'Screen Top' },
    { value: 'navigationBar', label: 'Header Bottom' },
  ],
  defaultValue: 'navigationBar',
  isNullable: true,
};

/** Paywall-only lower media boundary field. */
export const V2_MEDIA_BOTTOM_ALIGNMENT_FIELD: FieldConfig = {
  path: 'content.featuredMedia.appearance.bottomAlignment',
  label: 'Media Bottom',
  type: 'select',
  options: [
    { value: 'contentTop', label: 'Above Content' },
    { value: 'contentBottom', label: 'Behind Intro Content' },
  ],
  defaultValue: 'contentTop',
  isNullable: true,
};

/** Video-only repeat field (shown conditionally when mediaKind === 'Video'). */
export const V2_MEDIA_REPEAT_FIELD: FieldConfig = {
  path: 'content.featuredMedia.appearance.repeat',
  label: 'Repeat',
  type: 'select',
  options: [...BOOLEAN_OPTIONS],
  defaultValue: 'true',
  isNullable: true,
};
